import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const ReportsPage = () => {
    const [reportPeriod, setReportPeriod] = useState('daily');
    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [dateOffset, setDateOffset] = useState(0); // 0 = current period, -1 = one period back, etc.

    useEffect(() => {
        loadReportData(reportPeriod, dateOffset);
    }, [reportPeriod, dateOffset]);

    const loadReportData = async (period, offset) => {
        setIsLoading(true);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));

        // Generate dummy data based on period and offset
        const dummyData = generateDummyReportData(period, offset);
        setReportData(dummyData);
        setIsLoading(false);
    };

    const handlePeriodChange = (newPeriod) => {
        setReportPeriod(newPeriod);
        setDateOffset(0); // Reset offset when changing period type
    };

    const handleNavigateBack = () => {
        setDateOffset(prev => prev - 1);
    };

    const handleNavigateForward = () => {
        if (dateOffset < 0) {
            setDateOffset(prev => prev + 1);
        }
    };

    const isAtCurrentPeriod = dateOffset === 0;

    const generateDummyReportData = (period, offset = 0) => {
        // Generate realistic dummy data based on the period
        let multiplier = 1;
        let days = 1;
        if (period === 'weekly') {
            multiplier = 7;
            days = 7;
        }
        if (period === 'monthly') {
            multiplier = 30;
            days = 30;
        }

        const baseDetections = Math.floor(Math.random() * 50 + 100) * multiplier;
        const uniqueVehicles = Math.floor(baseDetections * 0.65);
        const repeatVisitors = baseDetections - uniqueVehicles;
        const driveOffCount = Math.floor(Math.random() * 3) * multiplier;
        const driveOffAmount = driveOffCount * (Math.random() * 80 + 40);
        const stolenVehicles = Math.floor(Math.random() * 2);
        const regFailures = Math.floor(Math.random() * 5 + 3) * multiplier;

        // Generate chart data based on period and offset
        const chartData = period === 'daily' ? generateHourlyChartData(offset) : generateChartData(days, offset);

        return {
            period: period,
            summary: {
                totalDetections: baseDetections,
                uniqueVehicles: uniqueVehicles,
                repeatVisitors: repeatVisitors,
                driveOffs: {
                    count: driveOffCount,
                    totalAmount: driveOffAmount,
                    settled: Math.floor(driveOffCount * 0.3),
                    outstanding: Math.ceil(driveOffCount * 0.7)
                },
                stolenVehicles: stolenVehicles,
                registrationFailures: regFailures
            },
            topVehicles: [
                { plate: 'ABC123', visits: 15 * multiplier, lastSeen: '2 hours ago' },
                { plate: 'XYZ789', visits: 12 * multiplier, lastSeen: '5 hours ago' },
                { plate: 'DEF456', visits: 10 * multiplier, lastSeen: '1 day ago' }
            ],
            peakHours: {
                morning: Math.floor(baseDetections * 0.25),
                afternoon: Math.floor(baseDetections * 0.45),
                evening: Math.floor(baseDetections * 0.20),
                night: Math.floor(baseDetections * 0.10)
            },
            chartData: chartData
        };
    };

    const generateChartData = (days, offset = 0) => {
        const labels = [];
        const newVehicles = [];
        const returningCustomers = [];
        const threats = [];

        const today = new Date();
        
        // Calculate the end date based on offset
        // offset 0 = today, -1 = yesterday's period, -2 = 2 periods ago, etc.
        const periodShift = Math.abs(offset) * days;
        
        for (let i = days - 1; i >= 0; i--) {
            const date = new Date(today);
            date.setDate(date.getDate() - i - periodShift);
            
            // Format date as "Mon 15" or just day number for 30 days
            const label = days <= 7 
                ? date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })
                : date.getDate().toString();
            
            labels.push(label);
            
            // Generate random data with some realistic patterns
            const baseTraffic = Math.floor(Math.random() * 30 + 50); // 50-80 vehicles per day
            const newCount = Math.floor(baseTraffic * (0.4 + Math.random() * 0.2)); // 40-60% new
            const returningCount = Math.floor(baseTraffic * (0.35 + Math.random() * 0.15)); // 35-50% returning
            const threatCount = Math.floor(Math.random() * 3); // 0-2 threats per day
            
            newVehicles.push(newCount);
            returningCustomers.push(returningCount);
            threats.push(threatCount);
        }

        return {
            labels,
            datasets: [
                {
                    label: 'New Vehicles',
                    data: newVehicles,
                    borderColor: '#000000',
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    borderWidth: 2,
                    tension: 0.4,
                    fill: true
                },
                {
                    label: 'Returning Customers',
                    data: returningCustomers,
                    borderColor: '#198754',
                    backgroundColor: 'rgba(25, 135, 84, 0.1)',
                    borderWidth: 2,
                    tension: 0.4,
                    fill: true
                },
                {
                    label: 'Threats',
                    data: threats,
                    borderColor: '#dc3545',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    borderWidth: 2,
                    tension: 0.4,
                    fill: true
                }
            ]
        };
    };

    const generateHourlyChartData = (offset = 0) => {
        const labels = [];
        const newVehicles = [];
        const returningCustomers = [];
        const threats = [];

        // offset determines which day: 0 = today, -1 = yesterday, -2 = 2 days ago, etc.
        // This is just for display purposes in dummy data
        
        // Generate data for 24 hours (0-23)
        for (let hour = 0; hour < 24; hour++) {
            // Format hour as "12 AM", "1 AM", etc.
            const ampm = hour < 12 ? 'AM' : 'PM';
            const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            labels.push(`${displayHour} ${ampm}`);
            
            // Realistic traffic patterns with low activity from 11pm (23) to 5am (5)
            let baseTraffic;
            
            if (hour >= 23 || hour <= 5) {
                // Night hours (11pm - 5am): Very low traffic
                baseTraffic = Math.floor(Math.random() * 3 + 1); // 1-3 vehicles
            } else if (hour >= 6 && hour <= 8) {
                // Morning rush (6am - 8am): High traffic
                baseTraffic = Math.floor(Math.random() * 15 + 20); // 20-35 vehicles
            } else if (hour >= 12 && hour <= 14) {
                // Lunch time (12pm - 2pm): Peak traffic
                baseTraffic = Math.floor(Math.random() * 20 + 30); // 30-50 vehicles
            } else if (hour >= 17 && hour <= 19) {
                // Evening rush (5pm - 7pm): High traffic
                baseTraffic = Math.floor(Math.random() * 18 + 25); // 25-43 vehicles
            } else if (hour >= 20 && hour <= 22) {
                // Evening (8pm - 10pm): Moderate traffic
                baseTraffic = Math.floor(Math.random() * 10 + 10); // 10-20 vehicles
            } else {
                // Other hours: Normal traffic
                baseTraffic = Math.floor(Math.random() * 12 + 15); // 15-27 vehicles
            }
            
            // Calculate distribution
            const newCount = Math.floor(baseTraffic * (0.4 + Math.random() * 0.2)); // 40-60% new
            const returningCount = Math.floor(baseTraffic * (0.35 + Math.random() * 0.15)); // 35-50% returning
            
            // Threats are rare and mostly during night hours
            let threatCount = 0;
            if (hour >= 23 || hour <= 5) {
                threatCount = Math.random() < 0.3 ? 1 : 0; // 30% chance of 1 threat during night
            } else {
                threatCount = Math.random() < 0.1 ? 1 : 0; // 10% chance during day
            }
            
            newVehicles.push(newCount);
            returningCustomers.push(returningCount);
            threats.push(threatCount);
        }

        return {
            labels,
            datasets: [
                {
                    label: 'New Vehicles',
                    data: newVehicles,
                    borderColor: '#000000',
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    borderWidth: 2,
                    tension: 0.4,
                    fill: true
                },
                {
                    label: 'Returning Customers',
                    data: returningCustomers,
                    borderColor: '#198754',
                    backgroundColor: 'rgba(25, 135, 84, 0.1)',
                    borderWidth: 2,
                    tension: 0.4,
                    fill: true
                },
                {
                    label: 'Threats',
                    data: threats,
                    borderColor: '#dc3545',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    borderWidth: 2,
                    tension: 0.4,
                    fill: true
                }
            ]
        };
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'AUD',
        }).format(value);
    };

    const getPeriodLabel = () => {
        const today = new Date();
        const periodDays = reportPeriod === 'daily' ? 1 : reportPeriod === 'weekly' ? 7 : 30;
        const daysBack = Math.abs(dateOffset) * periodDays;
        
        if (dateOffset === 0) {
            // Current period
            switch(reportPeriod) {
                case 'daily': return 'Today';
                case 'weekly': return 'Last 7 Days';
                case 'monthly': return 'Last 30 Days';
                default: return '';
            }
        } else {
            // Historical period
            const endDate = new Date(today);
            endDate.setDate(endDate.getDate() - daysBack);
            
            if (reportPeriod === 'daily') {
                // For daily: "Yesterday", "Nov 13, 2025", etc.
                if (dateOffset === -1) {
                    return 'Yesterday';
                }
                return endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            } else {
                // For weekly/monthly: show date range
                const startDate = new Date(endDate);
                startDate.setDate(startDate.getDate() - periodDays + 1);
                
                const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                
                return `${startStr} - ${endStr}`;
            }
        }
    };

    return (
        <div style={{minHeight: '100vh', backgroundColor: '#F8F9FA'}}>
            {/* Header */}
            <div style={{backgroundColor: '#1E70E0', color: 'white', padding: '2rem 0', boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                <div className="container">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="white" className="me-3">
                                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                            </svg>
                            <h1 className="mb-0 fw-bold" style={{fontSize: '1.75rem'}}>Activity Reports</h1>
                        </div>
                        <button 
                            className="btn btn-light"
                            onClick={() => window.close()}
                            style={{fontWeight: '500'}}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="container py-4">
                {/* Period Selector with Navigation Arrows */}
                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        {/* Back Arrow */}
                        <button
                            className="btn btn-outline-primary"
                            onClick={handleNavigateBack}
                            style={{
                                padding: '0.75rem 1rem',
                                fontSize: '1.5rem',
                                lineHeight: '1',
                                minWidth: '50px'
                            }}
                            title="Go back one period"
                        >
                            ◄
                        </button>

                        {/* Period Selector Buttons */}
                        <div className="btn-group flex-grow-1" role="group" style={{maxWidth: '600px'}}>
                            <button 
                                type="button" 
                                className={`btn ${reportPeriod === 'daily' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handlePeriodChange('daily')}
                                style={{padding: '0.75rem 1.5rem', fontSize: '1rem'}}
                            >
                                Today
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${reportPeriod === 'weekly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handlePeriodChange('weekly')}
                                style={{padding: '0.75rem 1.5rem', fontSize: '1rem'}}
                            >
                                Last 7 Days
                            </button>
                            <button 
                                type="button" 
                                className={`btn ${reportPeriod === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handlePeriodChange('monthly')}
                                style={{padding: '0.75rem 1.5rem', fontSize: '1rem'}}
                            >
                                Last 30 Days
                            </button>
                        </div>

                        {/* Forward Arrow */}
                        <button
                            className="btn btn-outline-primary"
                            onClick={handleNavigateForward}
                            disabled={isAtCurrentPeriod}
                            style={{
                                padding: '0.75rem 1rem',
                                fontSize: '1.5rem',
                                lineHeight: '1',
                                minWidth: '50px',
                                opacity: isAtCurrentPeriod ? 0.5 : 1,
                                cursor: isAtCurrentPeriod ? 'not-allowed' : 'pointer'
                            }}
                            title={isAtCurrentPeriod ? 'Already at current period' : 'Go forward one period'}
                        >
                            ►
                        </button>
                    </div>

                    {/* Period Label Display */}
                    {!isAtCurrentPeriod && (
                        <div className="text-center mt-3">
                            <span className="badge" style={{
                                fontSize: '1rem', 
                                padding: '0.5rem 1rem',
                                backgroundColor: '#1E70E0',
                                color: 'white'
                            }}>
                                Viewing: {getPeriodLabel()}
                            </span>
                        </div>
                    )}
                </div>

                {isLoading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary mb-2" role="status" style={{width: '3rem', height: '3rem'}}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mb-0" style={{fontSize: '1.125rem'}}>Loading report data...</p>
                    </div>
                ) : reportData ? (
                    <>
                        {/* Summary Cards */}
                        <div className="row g-4 mb-4">
                            <div className="col-md-3">
                                <div className="card border-primary h-100 shadow-sm">
                                    <div className="card-body text-center p-4">
                                        <h6 className="text-muted small mb-2 text-uppercase">Total Detections</h6>
                                        <h2 className="mb-0 text-primary fw-bold" style={{fontSize: '2.5rem'}}>{reportData.summary.totalDetections}</h2>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-success h-100 shadow-sm">
                                    <div className="card-body text-center p-4">
                                        <h6 className="text-muted small mb-2 text-uppercase">Unique Vehicles</h6>
                                        <h2 className="mb-0 text-success fw-bold" style={{fontSize: '2.5rem'}}>{reportData.summary.uniqueVehicles}</h2>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-danger h-100 shadow-sm">
                                    <div className="card-body text-center p-4">
                                        <h6 className="text-muted small mb-2 text-uppercase">Drive-Offs</h6>
                                        <h2 className="mb-0 text-danger fw-bold" style={{fontSize: '2.5rem'}}>{reportData.summary.driveOffs.count}</h2>
                                        <p className="small text-muted mb-0 mt-2">
                                            {formatCurrency(reportData.summary.driveOffs.totalAmount)} owed
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card border-warning h-100 shadow-sm">
                                    <div className="card-body text-center p-4">
                                        <h6 className="text-muted small mb-2 text-uppercase">Repeat Visitors</h6>
                                        <h2 className="mb-0 text-warning fw-bold" style={{fontSize: '2.5rem'}}>{reportData.summary.repeatVisitors}</h2>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Vehicle Activity Chart - Show for all periods */}
                        {reportData.chartData && (
                            <div className="card mb-4 shadow-sm">
                                <div className="card-header bg-light py-3">
                                    <h5 className="mb-0 fw-semibold">
                                        {reportPeriod === 'daily' 
                                            ? 'Hourly Vehicle Activity - Today' 
                                            : `Vehicle Activity Trend - ${getPeriodLabel()}`
                                        }
                                    </h5>
                                </div>
                                <div className="card-body p-4">
                                    <Line 
                                        data={reportData.chartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: true,
                                            aspectRatio: 2.5,
                                            plugins: {
                                                legend: {
                                                    position: 'top',
                                                    labels: {
                                                        font: {
                                                            size: 13,
                                                            family: 'Inter, sans-serif'
                                                        },
                                                        padding: 15,
                                                        usePointStyle: true
                                                    }
                                                },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                    padding: 12,
                                                    titleFont: {
                                                        size: 14,
                                                        family: 'Inter, sans-serif'
                                                    },
                                                    bodyFont: {
                                                        size: 13,
                                                        family: 'Inter, sans-serif'
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            let label = context.dataset.label || '';
                                                            if (label) {
                                                                label += ': ';
                                                            }
                                                            label += context.parsed.y + ' vehicles';
                                                            return label;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                y: {
                                                    beginAtZero: true,
                                                    ticks: {
                                                        stepSize: 10,
                                                        font: {
                                                            size: 12,
                                                            family: 'Inter, sans-serif'
                                                        }
                                                    },
                                                    grid: {
                                                        color: 'rgba(0, 0, 0, 0.05)'
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Number of Vehicles',
                                                        font: {
                                                            size: 13,
                                                            weight: '600',
                                                            family: 'Inter, sans-serif'
                                                        }
                                                    }
                                                },
                                                x: {
                                                    ticks: {
                                                        font: {
                                                            size: 12,
                                                            family: 'Inter, sans-serif'
                                                        }
                                                    },
                                                    grid: {
                                                        display: false
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: reportPeriod === 'daily' ? 'Hour of Day' : (reportPeriod === 'weekly' ? 'Day' : 'Date'),
                                                        font: {
                                                            size: 13,
                                                            weight: '600',
                                                            family: 'Inter, sans-serif'
                                                        }
                                                    }
                                                }
                                            },
                                            interaction: {
                                                mode: 'index',
                                                intersect: false
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Financial Summary */}
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-light py-3">
                                <h5 className="mb-0 fw-semibold">Financial Summary - {getPeriodLabel()}</h5>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-4">
                                    <div className="col-md-4">
                                        <div className="text-center p-4 bg-light rounded">
                                            <p className="text-muted mb-2">Outstanding Drive-Offs</p>
                                            <h3 className="text-danger mb-0 fw-bold">{formatCurrency(reportData.summary.driveOffs.totalAmount)}</h3>
                                            <p className="text-muted small mb-0 mt-2">{reportData.summary.driveOffs.outstanding} cases</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="text-center p-4 bg-light rounded">
                                            <p className="text-muted mb-2">Settled This Period</p>
                                            <h3 className="text-success mb-0 fw-bold">{reportData.summary.driveOffs.settled}</h3>
                                            <p className="text-muted small mb-0 mt-2">cases resolved</p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="text-center p-4 bg-light rounded">
                                            <p className="text-muted mb-2">Recovery Rate</p>
                                            <h3 className="text-primary mb-0 fw-bold">
                                                {reportData.summary.driveOffs.count > 0 
                                                    ? Math.round((reportData.summary.driveOffs.settled / reportData.summary.driveOffs.count) * 100)
                                                    : 0}%
                                            </h3>
                                            <p className="text-muted small mb-0 mt-2">of cases</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-4 mb-4">
                            {/* Top Frequent Visitors */}
                            <div className="col-md-6">
                                <div className="card h-100 shadow-sm">
                                    <div className="card-header bg-light py-3">
                                        <h6 className="mb-0 fw-semibold">Top Frequent Visitors</h6>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="list-group list-group-flush">
                                            {reportData.topVehicles.map((vehicle, index) => (
                                                <div key={index} className="list-group-item d-flex justify-content-between align-items-center px-0 py-3">
                                                    <div>
                                                        <strong style={{fontSize: '1.125rem'}}>{vehicle.plate}</strong>
                                                        <br />
                                                        <small className="text-muted">Last seen: {vehicle.lastSeen}</small>
                                                    </div>
                                                    <span className="badge bg-primary rounded-pill" style={{fontSize: '0.875rem', padding: '0.5rem 1rem'}}>{vehicle.visits} visits</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Peak Hours */}
                            <div className="col-md-6">
                                <div className="card h-100 shadow-sm">
                                    <div className="card-header bg-light py-3">
                                        <h6 className="mb-0 fw-semibold">Activity by Time of Day</h6>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="mb-4">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span>Morning (6am-12pm)</span>
                                                <strong>{reportData.peakHours.morning}</strong>
                                            </div>
                                            <div className="progress" style={{height: '25px'}}>
                                                <div className="progress-bar bg-info" style={{width: `${(reportData.peakHours.morning / reportData.summary.totalDetections) * 100}%`}}></div>
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span>Afternoon (12pm-6pm)</span>
                                                <strong>{reportData.peakHours.afternoon}</strong>
                                            </div>
                                            <div className="progress" style={{height: '25px'}}>
                                                <div className="progress-bar bg-success" style={{width: `${(reportData.peakHours.afternoon / reportData.summary.totalDetections) * 100}%`}}></div>
                                            </div>
                                        </div>
                                        <div className="mb-4">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span>Evening (6pm-12am)</span>
                                                <strong>{reportData.peakHours.evening}</strong>
                                            </div>
                                            <div className="progress" style={{height: '25px'}}>
                                                <div className="progress-bar bg-warning" style={{width: `${(reportData.peakHours.evening / reportData.summary.totalDetections) * 100}%`}}></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span>Night (12am-6am)</span>
                                                <strong>{reportData.peakHours.night}</strong>
                                            </div>
                                            <div className="progress" style={{height: '25px'}}>
                                                <div className="progress-bar bg-secondary" style={{width: `${(reportData.peakHours.night / reportData.summary.totalDetections) * 100}%`}}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Risk Alerts */}
                        {(reportData.summary.stolenVehicles > 0 || reportData.summary.registrationFailures > 0) && (
                            <div className="alert alert-warning mb-4 shadow-sm">
                                <h6 className="alert-heading fw-semibold">Security Alerts - {getPeriodLabel()}</h6>
                                {reportData.summary.stolenVehicles > 0 && (
                                    <p className="mb-2">
                                        <strong>{reportData.summary.stolenVehicles} stolen vehicle(s) detected</strong>
                                        <br />
                                        <small className="text-muted">Authorities were notified</small>
                                    </p>
                                )}
                                {reportData.summary.registrationFailures > 0 && (
                                    <p className="mb-0">
                                        <strong>{reportData.summary.registrationFailures} registration check failure(s)</strong>
                                        <br />
                                        <small className="text-muted">These vehicles may require attention</small>
                                    </p>
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="text-center py-5 text-muted">
                        <p>No report data available</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportsPage;

